/**
 * Página de Edição de Item de Checklist - ConfereAI
 * Alinhada com a estrutura da tabela checklist_itens do PostgreSQL
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../../../services';
import { utils } from '../../../utils';
import ViewLayout from '../../../components/Common/ViewLayout';

// Interface alinhada com a tabela checklist_itens do PostgreSQL
interface ChecklistItemFormData {
  id: number;
  checklist_id: number | null;
  categoria: string | null;
  descricao: string;
  tipo: string;
  opcoes: any | null;
  obrigatorio: boolean;
  ordem: number | null;
  observacao: string | null;
}

interface GrupoData {
  id: number;
  nome: string;
  tipo: string;
  status: string;
  item_ordem?: number;
}

const ChecklistItemEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [formData, setFormData] = useState<ChecklistItemFormData>({
    id: 0,
    checklist_id: null,
    categoria: '',
    descricao: '',
    tipo: 'texto',
    opcoes: null,
    obrigatorio: false,
    ordem: null,
    observacao: ''
  });

  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [opcoes, setOpcoes] = useState<string[]>([]);
  const [opcaoInput, setOpcaoInput] = useState('');

  // Estado para grupos
  const [allGroups, setAllGroups] = useState<GrupoData[]>([]);
  const [linkedGroups, setLinkedGroups] = useState<GrupoData[]>([]);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);

  const tiposDisponiveis = [
    { value: 'texto', label: 'Texto' },
    { value: 'numero', label: 'Número' },
    { value: 'selecao', label: 'Seleção' },
    { value: 'multipla_escolha', label: 'Múltipla Escolha' },
    { value: 'foto', label: 'Foto' },
    { value: 'assinatura', label: 'Assinatura' }
  ];

  const categoriasComuns = [
    'Segurança',
    'Manutenção',
    'Documentação',
    'Inspeção Visual',
    'Sistema Elétrico',
    'Sistema Mecânico',
    'Limpeza',
    'Equipamentos'
  ];

  const loadGroups = useCallback(async () => {
    try {
      setIsLoadingGroups(true);
      const [allGroupsRes, linkedGroupsRes] = await Promise.all([
        apiService.getChecklistGroups({ limit: 100, status: 'Ativo' }),
        apiService.getGroupsByItem(parseInt(id!))
      ]);

      if (allGroupsRes && allGroupsRes.data) {
        const grupos = (allGroupsRes.data as any).grupos || allGroupsRes.data;
        setAllGroups(Array.isArray(grupos) ? grupos : []);
      }

      if (linkedGroupsRes && linkedGroupsRes.data) {
        setLinkedGroups(Array.isArray(linkedGroupsRes.data) ? linkedGroupsRes.data : []);
      }
    } catch (error: any) {
      console.error('Erro ao carregar grupos:', error);
    } finally {
      setIsLoadingGroups(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadChecklistItem();
      loadGroups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadChecklistItem = async () => {
    try {
      setIsLoadingData(true);
      const response = await apiService.getChecklistItemById(parseInt(id!));
      if (response.success && response.data) {
        const item = response.data as any;
        setFormData({
          id: item.id,
          checklist_id: item.checklist_id,
          categoria: item.categoria || '',
          descricao: item.descricao,
          tipo: item.tipo,
          opcoes: item.opcoes,
          obrigatorio: item.obrigatorio,
          ordem: item.ordem,
          observacao: item.observacao || ''
        });

        // Carregar opções se existirem
        if (item.opcoes && Array.isArray(item.opcoes)) {
          setOpcoes(item.opcoes);
        }
      } else {
        throw new Error('Item não encontrado');
      }
    } catch (error: any) {
      utils.showNotification(error.message || 'Erro ao carregar item', { type: 'error' });
      navigate('/itens-checklist');
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else if (name === 'ordem') {
      setFormData(prev => ({
        ...prev,
        [name]: value ? parseInt(value) : null
      }));
    } else if (name === 'checklist_id') {
      setFormData(prev => ({
        ...prev,
        [name]: value ? parseInt(value) : null
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleAdicionarOpcao = () => {
    if (opcaoInput.trim()) {
      setOpcoes(prev => [...prev, opcaoInput.trim()]);
      setOpcaoInput('');
    }
  };

  const handleRemoverOpcao = (index: number) => {
    setOpcoes(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddToGroup = async (groupId: number) => {
    try {
      await apiService.addItemToGroup(groupId, parseInt(id!), 0);
      utils.showNotification('Item adicionado ao grupo com sucesso!', { type: 'success' });
      await loadGroups();
    } catch (error: any) {
      utils.showNotification(error.message || 'Erro ao adicionar item ao grupo', { type: 'error' });
    }
  };

  const handleRemoveFromGroup = async (groupId: number) => {
    try {
      await apiService.removeItemFromGroup(groupId, parseInt(id!));
      utils.showNotification('Item removido do grupo.', { type: 'success' });
      await loadGroups();
    } catch (error: any) {
      utils.showNotification(error.message || 'Erro ao remover item do grupo', { type: 'error' });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.descricao.trim()) {
      newErrors.descricao = 'Descrição é obrigatória';
    }

    if (!formData.tipo) {
      newErrors.tipo = 'Tipo é obrigatório';
    }

    if ((formData.tipo === 'selecao' || formData.tipo === 'multipla_escolha') && opcoes.length === 0) {
      newErrors.opcoes = 'Adicione pelo menos uma opção para este tipo de campo';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      utils.showNotification('Por favor, corrija os erros no formulário', { type: 'error' });
      return;
    }

    setIsLoading(true);
    try {
      // Preparar dados para envio - converter null para undefined para compatibilidade com API
      const dataToSend: any = {
        descricao: formData.descricao,
        tipo: formData.tipo,
        obrigatorio: formData.obrigatorio,
        categoria: formData.categoria || undefined,
        ordem: formData.ordem || undefined,
        observacao: formData.observacao || undefined,
        checklist_id: formData.checklist_id || undefined,
        opcoes: (formData.tipo === 'selecao' || formData.tipo === 'multipla_escolha')
          ? opcoes
          : undefined
      };

      const response = await apiService.updateChecklistItem(formData.id, dataToSend);
      if (response.success) {
        utils.showNotification('Item de checklist atualizado com sucesso!', { type: 'success' });
        navigate('/itens-checklist');
      } else {
        throw new Error(response.message || 'Erro ao atualizar item');
      }
    } catch (error: any) {
      utils.showNotification(error.message || 'Erro ao atualizar item', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/itens-checklist');
  };

  const mostrarCampoOpcoes = formData.tipo === 'selecao' || formData.tipo === 'multipla_escolha';

  // Filtrar grupos disponíveis (que o item ainda não pertence)
  const linkedGroupIds = new Set(linkedGroups.map(g => g.id));
  const availableGroups = allGroups.filter(g => !linkedGroupIds.has(g.id));

  if (isLoadingData) {
    return (
      <ViewLayout title="Editar Item de Checklist" subtitle="Carregando..." onBack={handleCancel} isLoading={true}>
        <div style={{ textAlign: 'center', padding: '50px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando dados do item...</p>
        </div>
      </ViewLayout>
    );
  }

  return (
    <ViewLayout title="Editar Item de Checklist" subtitle={`Editando: ${formData.descricao}`} onBack={handleCancel} isLoading={isLoading}>
      <form onSubmit={handleSubmit}>
        <div className="form-grid">
          {/* Descrição */}
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label htmlFor="descricao">Descrição *</label>
            <textarea
              id="descricao"
              name="descricao"
              value={formData.descricao}
              onChange={handleInputChange}
              className={`form-control ${errors.descricao ? 'error' : ''}`}
              placeholder="Descrição do item a ser verificado"
              rows={3}
            />
            {errors.descricao && <div className="error-message">{errors.descricao}</div>}
          </div>

          {/* Tipo */}
          <div className="form-group">
            <label htmlFor="tipo">Tipo *</label>
            <select
              id="tipo"
              name="tipo"
              value={formData.tipo}
              onChange={handleInputChange}
              className={`form-control ${errors.tipo ? 'error' : ''}`}
            >
              {tiposDisponiveis.map(tipo => (
                <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
              ))}
            </select>
            {errors.tipo && <div className="error-message">{errors.tipo}</div>}
          </div>

          {/* Categoria */}
          <div className="form-group">
            <label htmlFor="categoria">Categoria</label>
            <input
              id="categoria"
              type="text"
              name="categoria"
              value={formData.categoria || ''}
              onChange={handleInputChange}
              className="form-control"
              placeholder="Ex: Segurança, Manutenção"
              list="categorias-list"
            />
            <datalist id="categorias-list">
              {categoriasComuns.map(cat => (
                <option key={cat} value={cat} />
              ))}
            </datalist>
          </div>

          {/* Ordem */}
          <div className="form-group">
            <label htmlFor="ordem">Ordem de Exibição</label>
            <input
              id="ordem"
              type="number"
              name="ordem"
              value={formData.ordem || ''}
              onChange={handleInputChange}
              className="form-control"
              placeholder="Ex: 1, 2, 3..."
              min="1"
            />
          </div>

          {/* Obrigatório */}
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="obrigatorio"
                checked={formData.obrigatorio}
                onChange={handleInputChange}
              />
              <span>Item obrigatório</span>
            </label>
            <small style={{ display: 'block', color: '#7f8c8d', marginTop: '5px' }}>
              Marque se este item deve ser preenchido obrigatoriamente
            </small>
          </div>

          {/* Opções (para seleção e múltipla escolha) */}
          {mostrarCampoOpcoes && (
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Opções *</label>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                <input
                  type="text"
                  value={opcaoInput}
                  onChange={(e) => setOpcaoInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAdicionarOpcao())}
                  className="form-control"
                  placeholder="Digite uma opção e pressione Enter ou clique em Adicionar"
                />
                <button type="button" onClick={handleAdicionarOpcao} className="btn btn-secondary">
                  Adicionar
                </button>
              </div>
              {errors.opcoes && <div className="error-message">{errors.opcoes}</div>}

              {opcoes.length > 0 && (
                <div style={{ marginTop: '10px' }}>
                  <strong>Opções adicionadas:</strong>
                  <ul style={{ listStyle: 'none', padding: 0, marginTop: '10px' }}>
                    {opcoes.map((opcao, index) => (
                      <li key={index} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 12px',
                        background: '#f8f9fa',
                        borderRadius: '4px',
                        marginBottom: '5px'
                      }}>
                        <span>{opcao}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoverOpcao(index)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#e74c3c',
                            cursor: 'pointer',
                            fontSize: '1.2rem'
                          }}
                          title="Remover opção"
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Observação */}
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label htmlFor="observacao">Observações</label>
            <textarea
              id="observacao"
              name="observacao"
              value={formData.observacao || ''}
              onChange={handleInputChange}
              className="form-control"
              placeholder="Instruções adicionais ou observações sobre este item"
              rows={2}
            />
          </div>

          {/* Grupos de Checklist */}
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label>
              <i className="fas fa-layer-group" style={{ marginRight: '6px', color: '#04C64F' }}></i>
              Grupos de Checklist
            </label>
            <small style={{ display: 'block', color: '#7f8c8d', marginBottom: '10px' }}>
              Selecione os grupos aos quais este item pertence. O item será automaticamente incluído nos grupos selecionados.
            </small>

            {isLoadingGroups ? (
              <div style={{ textAlign: 'center', padding: '15px' }}>
                <span className="loading"></span>
                <p style={{ fontSize: '13px', color: '#666' }}>Carregando grupos...</p>
              </div>
            ) : (
              <>
                {/* Grupos vinculados */}
                {linkedGroups.length > 0 && (
                  <div style={{ marginBottom: '15px' }}>
                    <strong style={{ fontSize: '13px', color: '#2c3e50', display: 'block', marginBottom: '8px' }}>
                      Grupos vinculados:
                    </strong>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {linkedGroups.map(group => (
                        <span
                          key={group.id}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '6px 12px',
                            backgroundColor: '#e8f5e9',
                            border: '1px solid #a5d6a7',
                            borderRadius: '20px',
                            fontSize: '13px',
                            color: '#2e7d32'
                          }}
                        >
                          <i className="fas fa-check-circle" style={{ fontSize: '11px' }}></i>
                          {group.nome}
                          <span style={{ fontSize: '10px', color: '#66bb6a' }}>({group.tipo})</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveFromGroup(group.id)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#c62828',
                              cursor: 'pointer',
                              padding: '0 2px',
                              fontSize: '14px',
                              lineHeight: 1
                            }}
                            title="Remover deste grupo"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Seletor de grupo para adicionar */}
                {availableGroups.length > 0 && (
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
                    <div style={{ flex: 1 }}>
                      <select
                        id="grupo-select"
                        className="form-control"
                        defaultValue=""
                        onChange={(e) => {
                          if (e.target.value) {
                            handleAddToGroup(parseInt(e.target.value));
                            e.target.value = '';
                          }
                        }}
                      >
                        <option value="">Selecione um grupo para adicionar...</option>
                        {availableGroups.map(group => (
                          <option key={group.id} value={group.id}>
                            {group.nome} ({group.tipo})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {allGroups.length === 0 && linkedGroups.length === 0 && (
                  <div style={{
                    padding: '15px',
                    backgroundColor: '#fff3e0',
                    borderRadius: '8px',
                    border: '1px solid #ffe0b2',
                    fontSize: '13px',
                    color: '#e65100'
                  }}>
                    Nenhum grupo de checklist disponível. <a href="/grupos-checklist/novo" style={{ color: '#1565c0' }}>Crie um grupo</a> primeiro.
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="form-actions">
          <button type="button" onClick={handleCancel} className="btn btn-secondary">
            Cancelar
          </button>
          <button type="submit" className="btn btn-primary" disabled={isLoading}>
            {isLoading ? 'Atualizando...' : 'Atualizar Item'}
          </button>
        </div>
      </form>
    </ViewLayout>
  );
};

export default ChecklistItemEdit;
